import { useEffect, useMemo } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { useProfileStore } from '../stores/profileStore';
import { useMFAStore } from '../stores/mfaStore';
import { AuthMFA } from '../components/auth/AuthMFA';
import { useMFARequired } from '../hooks/useMFARequired';

/**
 * Guards against open-redirects (via `location.state.from` and the `?redirect=` query param)
 * by resolving the candidate against the current origin with the browser's own `URL` parser
 * and rejecting anything that doesn't resolve back to that same origin.
 *
 * A plain prefix check (e.g. `from.startsWith('/') && !from.startsWith('//')`) is NOT enough:
 * strings like `/\evil.com`, `/\/evil.com`, or `/\t/evil.com` (embedded tab/CR) all pass such a
 * check, yet the browser normalizes backslashes to forward slashes and strips embedded
 * whitespace/control characters when resolving a URL, so they resolve to `https://evil.com`.
 * `@remix-run/router` (under react-router v6) wraps `history.pushState` in a try/catch and
 * falls back to `window.location.assign(url)` for any non-`DataCloneError` failure -- and a
 * cross-origin-resolving path makes `pushState` throw `SecurityError`, so `navigate()` on an
 * unvalidated string like this can actually leave the SPA, not just fail silently. Using `URL`
 * to resolve the candidate applies the same normalization the browser/router use internally,
 * so anything that would escape the origin is caught here first.
 */
function resolveSameOriginPath(candidate: unknown): string | null {
  if (typeof candidate !== 'string' || candidate.length === 0) return null;
  try {
    const resolved = new URL(candidate, window.location.origin);
    if (resolved.origin !== window.location.origin) return null;
    const path = `${resolved.pathname}${resolved.search}${resolved.hash}`;
    // The origin check alone isn't sufficient: dot-segment normalization can resolve a
    // same-origin candidate (e.g. "/a/..//evil.com") down to a pathname that is itself
    // protocol-relative ("//evil.com"). That string would pass the origin check yet still
    // trip the same pushState -> SecurityError -> window.location.assign escape this function
    // exists to prevent, so the final serialized path must also be root-relative and single-slash.
    if (!path.startsWith('/') || path.startsWith('//')) return null;
    return path;
  } catch {
    return null;
  }
}

export default function LoginHandler() {
  const { profile, loading } = useProfileStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { requireMFA, checking } = useMFARequired();
  const { markMfaVerified } = useMFAStore();

  const [searchParams] = useSearchParams();
  const redirectPath = useMemo(() => {
    const url = searchParams.get('redirect');
    if (!url) return null;
    try {
      // The param may arrive URL-encoded; decode before validating it resolves same-origin.
      return resolveSameOriginPath(decodeURIComponent(url));
    } catch {
      return null;
    }
  }, [searchParams]);

  const fromPath = useMemo(
    () => resolveSameOriginPath((location.state as { from?: unknown } | null)?.from),
    [location.state]
  );

  // `?redirect=` takes priority over `location.state.from` if both are present.
  const targetPath = redirectPath ?? fromPath ?? '/dashboard';

  useEffect(() => {
    // `checking` must gate this too: useMFARequired() starts as { checking: true, requireMFA:
    // false } while listFactors() is pending, so without this guard the effect can navigate to
    // targetPath before MFA status is actually known -- including to routes like the standalone
    // print page that aren't wrapped in PrivateRoute and don't independently re-check MFA.
    if (!loading && profile && profile.last_name && !checking && !requireMFA) {
      navigate(targetPath, { replace: true });
    }
  }, [loading, profile, checking, requireMFA, navigate, targetPath]);

  if (loading || checking) return <LoadingSpinner />;
  if (!profile || !profile.last_name) return <Navigate to="/signup" replace />;

  return requireMFA ? (
    <AuthMFA
      onVerified={() => {
        markMfaVerified();
        navigate(targetPath, { replace: true });
      }}
    />
  ) : (
    <LoadingSpinner size="large" />
  );
}
